import * as z from 'zod';

export const CreateLoyaltyEarningRuleRequest = z.object({
  earningRule: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Loyalty earning rule ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        sourceAppId: z
          .string()
          .describe(
            'ID of the app managing the earning rule. Can be a loyalty app ID or a Wix automations app ID.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        triggerAppId: z
          .string()
          .describe(
            'ID of the app that triggers point assignment. Examples: Wix Stores, Wix Bookings, Wix Events.'
          )
          .min(1)
          .max(80),
        triggerActivityType: z
          .string()
          .describe(
            'Type of activity that triggers point assignment. For example, `wix-restaurants/orderSubmitted` or `birthday`.'
          )
          .min(1)
          .max(80),
        title: z.string().describe('Name of the earning rule.').min(1).max(50),
        status: z.enum(['UNKNOWN', 'ACTIVE', 'PAUSED']),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            'Revision number, incremented by 1 each time the earning rule is updated.\nPass the latest `revision` when updating to prevent conflicting changes.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the earning rule was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the earning rule was last updated.')
          .optional()
          .nullable(),
        metadata: z
          .object({
            canBeDeleted: z
              .boolean()
              .describe('Whether the earning rule can be deleted.')
              .optional(),
          })
          .describe('Additional metadata about the earning rule.')
          .optional(),
        triggerFilters: z
          .array(
            z.object({
              fieldKey: z
                .string()
                .describe(
                  'Key identifying the field in the trigger payload (e.g., "service_id", "eventId", "programId").'
                )
                .max(110)
                .optional(),
              values: z.array(z.string()).max(100).optional(),
            })
          )
          .max(100)
          .optional(),
      }),
      z.xor([
        z.object({
          conversionRate: z.never().optional(),
          fixedAmount: z
            .object({
              configs: z
                .array(
                  z.object({
                    tierId: z
                      .string()
                      .describe(
                        'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    points: z
                      .number()
                      .int()
                      .describe('Number of points to award.')
                      .optional(),
                  })
                )
                .max(21)
                .optional(),
            })
            .describe(
              'Fixed amount of points awarded for each qualifying activity.'
            ),
        }),
        z.object({
          fixedAmount: z.never().optional(),
          conversionRate: z
            .object({
              configs: z
                .array(
                  z.object({
                    tierId: z
                      .string()
                      .describe(
                        'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    moneyAmount: z
                      .number()
                      .int()
                      .describe(
                        'The amount of money used as a reference for point calculation.\nPoints are awarded proportionally to the amount spent.\n\nFor example, if set to 10, 1 point is awarded for every 10 units of currency spent (assuming `points` is set to 1).\n\nFormula for points is: `(amount spent) / (money_amount * points)`.'
                      )
                      .optional(),
                    points: z
                      .number()
                      .int()
                      .describe(
                        'Points given for the specified `money_amount`.\nWorks in conjunction with `money_amount` to define the earning rule.\n\nFor example: If `money_amount` is 20 and `points` is 10:\n- Spending 10 units of currency earns 5 points\n- Spending 20 units of currency earns 10 points\n- Spending 30 units of currency earns 15 points'
                      )
                      .optional(),
                  })
                )
                .max(21)
                .optional(),
              field: z
                .string()
                .describe(
                  'Specifies which field in the Wix automations trigger payload [REST](https://dev.wix.com/docs/rest/business-management/automations/introduction#how-do-automations-work)|[SDK](https://dev.wix.com/docs/sdk/backend-modules/automations/triggered-events/reporting-and-canceling-events) to use for calculating points in conversion rate rules.\nFor example, if set to "priceSummary.totalAmount", the rule uses the total order amount to calculate loyalty points to be awarded.\nThis field is only applicable for automated earning rules.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Points awarded based on a conversion rate formula: `(amount spent) / (money_amount * points)`.'
            ),
        }),
      ])
    )
    .describe('Earning rule to create.'),
});
export const CreateLoyaltyEarningRuleResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Loyalty earning rule ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    sourceAppId: z
      .string()
      .describe(
        'ID of the app managing the earning rule. Can be a loyalty app ID or a Wix automations app ID.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    triggerAppId: z
      .string()
      .describe(
        'ID of the app that triggers point assignment. Examples: Wix Stores, Wix Bookings, Wix Events.'
      )
      .min(1)
      .max(80)
      .optional(),
    triggerActivityType: z
      .string()
      .describe(
        'Type of activity that triggers point assignment. For example, `wix-restaurants/orderSubmitted` or `birthday`.'
      )
      .min(1)
      .max(80)
      .optional(),
    title: z
      .string()
      .describe('Name of the earning rule.')
      .min(1)
      .max(50)
      .optional(),
    status: z
      .enum(['UNKNOWN', 'ACTIVE', 'PAUSED'])
      .describe('Current status of the earning rule.')
      .optional(),
    revision: z
      .string()
      .regex(/^\d+$/, 'Must be a valid UInt64 string')
      .describe(
        'Revision number, incremented by 1 each time the earning rule is updated.\nPass the latest `revision` when updating to prevent conflicting changes.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the earning rule was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the earning rule was last updated.')
      .optional()
      .nullable(),
    metadata: z
      .object({
        canBeDeleted: z
          .boolean()
          .describe('Whether the earning rule can be deleted.')
          .optional(),
      })
      .describe('Additional metadata about the earning rule.')
      .optional(),
    triggerFilters: z
      .array(
        z.object({
          fieldKey: z
            .string()
            .describe(
              'Key identifying the field in the trigger payload (e.g., "service_id", "eventId", "programId").'
            )
            .max(110)
            .optional(),
          values: z.array(z.string()).max(100).optional(),
        })
      )
      .max(100)
      .optional(),
  }),
  z.xor([
    z.object({
      fixedAmount: z.never().optional(),
      conversionRate: z.never().optional(),
    }),
    z.object({
      conversionRate: z.never().optional(),
      fixedAmount: z
        .object({
          configs: z
            .array(
              z.object({
                tierId: z
                  .string()
                  .describe(
                    'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                points: z
                  .number()
                  .int()
                  .describe('Number of points to award.')
                  .optional(),
              })
            )
            .max(21)
            .optional(),
        })
        .describe(
          'Fixed amount of points awarded for each qualifying activity.'
        ),
    }),
    z.object({
      fixedAmount: z.never().optional(),
      conversionRate: z
        .object({
          configs: z
            .array(
              z.object({
                tierId: z
                  .string()
                  .describe(
                    'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                moneyAmount: z
                  .number()
                  .int()
                  .describe(
                    'The amount of money used as a reference for point calculation.\nPoints are awarded proportionally to the amount spent.\n\nFor example, if set to 10, 1 point is awarded for every 10 units of currency spent (assuming `points` is set to 1).\n\nFormula for points is: `(amount spent) / (money_amount * points)`.'
                  )
                  .optional(),
                points: z
                  .number()
                  .int()
                  .describe(
                    'Points given for the specified `money_amount`.\nWorks in conjunction with `money_amount` to define the earning rule.\n\nFor example: If `money_amount` is 20 and `points` is 10:\n- Spending 10 units of currency earns 5 points\n- Spending 20 units of currency earns 10 points\n- Spending 30 units of currency earns 15 points'
                  )
                  .optional(),
              })
            )
            .max(21)
            .optional(),
          field: z
            .string()
            .describe(
              'Specifies which field in the Wix automations trigger payload [REST](https://dev.wix.com/docs/rest/business-management/automations/introduction#how-do-automations-work)|[SDK](https://dev.wix.com/docs/sdk/backend-modules/automations/triggered-events/reporting-and-canceling-events) to use for calculating points in conversion rate rules.\nFor example, if set to "priceSummary.totalAmount", the rule uses the total order amount to calculate loyalty points to be awarded.\nThis field is only applicable for automated earning rules.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Points awarded based on a conversion rate formula: `(amount spent) / (money_amount * points)`.'
        ),
    }),
  ])
);
export const BulkCreateLoyaltyEarningRulesRequest = z.object({
  earningRules: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Loyalty earning rule ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          sourceAppId: z
            .string()
            .describe(
              'ID of the app managing the earning rule. Can be a loyalty app ID or a Wix automations app ID.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
          triggerAppId: z
            .string()
            .describe(
              'ID of the app that triggers point assignment. Examples: Wix Stores, Wix Bookings, Wix Events.'
            )
            .min(1)
            .max(80),
          triggerActivityType: z
            .string()
            .describe(
              'Type of activity that triggers point assignment. For example, `wix-restaurants/orderSubmitted` or `birthday`.'
            )
            .min(1)
            .max(80),
          title: z
            .string()
            .describe('Name of the earning rule.')
            .min(1)
            .max(50),
          status: z.enum(['UNKNOWN', 'ACTIVE', 'PAUSED']),
          revision: z
            .string()
            .regex(/^\d+$/, 'Must be a valid UInt64 string')
            .describe(
              'Revision number, incremented by 1 each time the earning rule is updated.\nPass the latest `revision` when updating to prevent conflicting changes.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the earning rule was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the earning rule was last updated.')
            .optional()
            .nullable(),
          metadata: z
            .object({
              canBeDeleted: z
                .boolean()
                .describe('Whether the earning rule can be deleted.')
                .optional(),
            })
            .describe('Additional metadata about the earning rule.')
            .optional(),
          triggerFilters: z
            .array(
              z.object({
                fieldKey: z
                  .string()
                  .describe(
                    'Key identifying the field in the trigger payload (e.g., "service_id", "eventId", "programId").'
                  )
                  .max(110)
                  .optional(),
                values: z.array(z.string()).max(100).optional(),
              })
            )
            .max(100)
            .optional(),
        }),
        z.xor([
          z.object({
            conversionRate: z.never().optional(),
            fixedAmount: z
              .object({
                configs: z
                  .array(
                    z.object({
                      tierId: z
                        .string()
                        .describe(
                          'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      points: z
                        .number()
                        .int()
                        .describe('Number of points to award.')
                        .optional(),
                    })
                  )
                  .max(21)
                  .optional(),
              })
              .describe(
                'Fixed amount of points awarded for each qualifying activity.'
              ),
          }),
          z.object({
            fixedAmount: z.never().optional(),
            conversionRate: z
              .object({
                configs: z
                  .array(
                    z.object({
                      tierId: z
                        .string()
                        .describe(
                          'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      moneyAmount: z
                        .number()
                        .int()
                        .describe(
                          'The amount of money used as a reference for point calculation.\nPoints are awarded proportionally to the amount spent.\n\nFor example, if set to 10, 1 point is awarded for every 10 units of currency spent (assuming `points` is set to 1).\n\nFormula for points is: `(amount spent) / (money_amount * points)`.'
                        )
                        .optional(),
                      points: z
                        .number()
                        .int()
                        .describe(
                          'Points given for the specified `money_amount`.\nWorks in conjunction with `money_amount` to define the earning rule.\n\nFor example: If `money_amount` is 20 and `points` is 10:\n- Spending 10 units of currency earns 5 points\n- Spending 20 units of currency earns 10 points\n- Spending 30 units of currency earns 15 points'
                        )
                        .optional(),
                    })
                  )
                  .max(21)
                  .optional(),
                field: z
                  .string()
                  .describe(
                    'Specifies which field in the Wix automations trigger payload [REST](https://dev.wix.com/docs/rest/business-management/automations/introduction#how-do-automations-work)|[SDK](https://dev.wix.com/docs/sdk/backend-modules/automations/triggered-events/reporting-and-canceling-events) to use for calculating points in conversion rate rules.\nFor example, if set to "priceSummary.totalAmount", the rule uses the total order amount to calculate loyalty points to be awarded.\nThis field is only applicable for automated earning rules.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Points awarded based on a conversion rate formula: `(amount spent) / (money_amount * points)`.'
              ),
          }),
        ])
      )
    )
    .min(1)
    .max(100),
});
export const BulkCreateLoyaltyEarningRulesResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Additional metadata for the created earning rules.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Loyalty earning rule ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              sourceAppId: z
                .string()
                .describe(
                  'ID of the app managing the earning rule. Can be a loyalty app ID or a Wix automations app ID.'
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              triggerAppId: z
                .string()
                .describe(
                  'ID of the app that triggers point assignment. Examples: Wix Stores, Wix Bookings, Wix Events.'
                )
                .min(1)
                .max(80)
                .optional(),
              triggerActivityType: z
                .string()
                .describe(
                  'Type of activity that triggers point assignment. For example, `wix-restaurants/orderSubmitted` or `birthday`.'
                )
                .min(1)
                .max(80)
                .optional(),
              title: z
                .string()
                .describe('Name of the earning rule.')
                .min(1)
                .max(50)
                .optional(),
              status: z
                .enum(['UNKNOWN', 'ACTIVE', 'PAUSED'])
                .describe('Current status of the earning rule.')
                .optional(),
              revision: z
                .string()
                .regex(/^\d+$/, 'Must be a valid UInt64 string')
                .describe(
                  'Revision number, incremented by 1 each time the earning rule is updated.\nPass the latest `revision` when updating to prevent conflicting changes.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the earning rule was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the earning rule was last updated.')
                .optional()
                .nullable(),
              metadata: z
                .object({
                  canBeDeleted: z
                    .boolean()
                    .describe('Whether the earning rule can be deleted.')
                    .optional(),
                })
                .describe('Additional metadata about the earning rule.')
                .optional(),
              triggerFilters: z
                .array(
                  z.object({
                    fieldKey: z
                      .string()
                      .describe(
                        'Key identifying the field in the trigger payload (e.g., "service_id", "eventId", "programId").'
                      )
                      .max(110)
                      .optional(),
                    values: z.array(z.string()).max(100).optional(),
                  })
                )
                .max(100)
                .optional(),
            }),
            z.xor([
              z.object({
                fixedAmount: z.never().optional(),
                conversionRate: z.never().optional(),
              }),
              z.object({
                conversionRate: z.never().optional(),
                fixedAmount: z
                  .object({
                    configs: z
                      .array(
                        z.object({
                          tierId: z
                            .string()
                            .describe(
                              'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          points: z
                            .number()
                            .int()
                            .describe('Number of points to award.')
                            .optional(),
                        })
                      )
                      .max(21)
                      .optional(),
                  })
                  .describe(
                    'Fixed amount of points awarded for each qualifying activity.'
                  ),
              }),
              z.object({
                fixedAmount: z.never().optional(),
                conversionRate: z
                  .object({
                    configs: z
                      .array(
                        z.object({
                          tierId: z
                            .string()
                            .describe(
                              'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                            )
                            .regex(
                              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                              'Must be a valid GUID'
                            )
                            .optional()
                            .nullable(),
                          moneyAmount: z
                            .number()
                            .int()
                            .describe(
                              'The amount of money used as a reference for point calculation.\nPoints are awarded proportionally to the amount spent.\n\nFor example, if set to 10, 1 point is awarded for every 10 units of currency spent (assuming `points` is set to 1).\n\nFormula for points is: `(amount spent) / (money_amount * points)`.'
                            )
                            .optional(),
                          points: z
                            .number()
                            .int()
                            .describe(
                              'Points given for the specified `money_amount`.\nWorks in conjunction with `money_amount` to define the earning rule.\n\nFor example: If `money_amount` is 20 and `points` is 10:\n- Spending 10 units of currency earns 5 points\n- Spending 20 units of currency earns 10 points\n- Spending 30 units of currency earns 15 points'
                            )
                            .optional(),
                        })
                      )
                      .max(21)
                      .optional(),
                    field: z
                      .string()
                      .describe(
                        'Specifies which field in the Wix automations trigger payload [REST](https://dev.wix.com/docs/rest/business-management/automations/introduction#how-do-automations-work)|[SDK](https://dev.wix.com/docs/sdk/backend-modules/automations/triggered-events/reporting-and-canceling-events) to use for calculating points in conversion rate rules.\nFor example, if set to "priceSummary.totalAmount", the rule uses the total order amount to calculate loyalty points to be awarded.\nThis field is only applicable for automated earning rules.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    'Points awarded based on a conversion rate formula: `(amount spent) / (money_amount * points)`.'
                  ),
              }),
            ])
          )
          .describe('Created earning rule.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Additional metadata for the created earning rules.')
    .optional(),
});
export const CreateCustomLoyaltyEarningRuleRequest = z.object({
  type: z.enum(['SOCIAL_MEDIA', 'BIRTHDAY']),
  options: z
    .object({
      earningRule: z
        .intersection(
          z.object({
            title: z
              .string()
              .describe('Name of the earning rule.')
              .min(1)
              .max(50)
              .optional(),
          }),
          z.xor([
            z.object({
              fixedAmount: z.never().optional(),
              conversionRate: z.never().optional(),
            }),
            z.object({
              conversionRate: z.never().optional(),
              fixedAmount: z
                .object({
                  configs: z
                    .array(
                      z.object({
                        tierId: z
                          .string()
                          .describe(
                            'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        points: z
                          .number()
                          .int()
                          .describe('Number of points to award.')
                          .optional(),
                      })
                    )
                    .max(21)
                    .optional(),
                })
                .describe(
                  'Fixed amount of points awarded for each qualifying activity.'
                ),
            }),
            z.object({
              fixedAmount: z.never().optional(),
              conversionRate: z
                .object({
                  configs: z
                    .array(
                      z.object({
                        tierId: z
                          .string()
                          .describe(
                            'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                          )
                          .regex(
                            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                            'Must be a valid GUID'
                          )
                          .optional()
                          .nullable(),
                        moneyAmount: z
                          .number()
                          .int()
                          .describe(
                            'The amount of money used as a reference for point calculation.\nPoints are awarded proportionally to the amount spent.\n\nFor example, if set to 10, 1 point is awarded for every 10 units of currency spent (assuming `points` is set to 1).\n\nFormula for points is: `(amount spent) / (money_amount * points)`.'
                          )
                          .optional(),
                        points: z
                          .number()
                          .int()
                          .describe(
                            'Points given for the specified `money_amount`.\nWorks in conjunction with `money_amount` to define the earning rule.\n\nFor example: If `money_amount` is 20 and `points` is 10:\n- Spending 10 units of currency earns 5 points\n- Spending 20 units of currency earns 10 points\n- Spending 30 units of currency earns 15 points'
                          )
                          .optional(),
                      })
                    )
                    .max(21)
                    .optional(),
                  field: z
                    .string()
                    .describe(
                      'Specifies which field in the Wix automations trigger payload [REST](https://dev.wix.com/docs/rest/business-management/automations/introduction#how-do-automations-work)|[SDK](https://dev.wix.com/docs/sdk/backend-modules/automations/triggered-events/reporting-and-canceling-events) to use for calculating points in conversion rate rules.\nFor example, if set to "priceSummary.totalAmount", the rule uses the total order amount to calculate loyalty points to be awarded.\nThis field is only applicable for automated earning rules.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Points awarded based on a conversion rate formula: `(amount spent) / (money_amount * points)`.'
                ),
            }),
          ])
        )
        .describe('Custom earning rule to create.')
        .optional(),
    })
    .optional(),
});
export const CreateCustomLoyaltyEarningRuleResponse = z.object({
  earningRule: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Loyalty earning rule ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        sourceAppId: z
          .string()
          .describe(
            'ID of the app managing the earning rule. Can be a loyalty app ID or a Wix automations app ID.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        triggerAppId: z
          .string()
          .describe(
            'ID of the app that triggers point assignment. Examples: Wix Stores, Wix Bookings, Wix Events.'
          )
          .min(1)
          .max(80)
          .optional(),
        triggerActivityType: z
          .string()
          .describe(
            'Type of activity that triggers point assignment. For example, `wix-restaurants/orderSubmitted` or `birthday`.'
          )
          .min(1)
          .max(80)
          .optional(),
        title: z
          .string()
          .describe('Name of the earning rule.')
          .min(1)
          .max(50)
          .optional(),
        status: z
          .enum(['UNKNOWN', 'ACTIVE', 'PAUSED'])
          .describe('Current status of the earning rule.')
          .optional(),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            'Revision number, incremented by 1 each time the earning rule is updated.\nPass the latest `revision` when updating to prevent conflicting changes.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the earning rule was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the earning rule was last updated.')
          .optional()
          .nullable(),
        metadata: z
          .object({
            canBeDeleted: z
              .boolean()
              .describe('Whether the earning rule can be deleted.')
              .optional(),
          })
          .describe('Additional metadata about the earning rule.')
          .optional(),
        triggerFilters: z
          .array(
            z.object({
              fieldKey: z
                .string()
                .describe(
                  'Key identifying the field in the trigger payload (e.g., "service_id", "eventId", "programId").'
                )
                .max(110)
                .optional(),
              values: z.array(z.string()).max(100).optional(),
            })
          )
          .max(100)
          .optional(),
      }),
      z.xor([
        z.object({
          fixedAmount: z.never().optional(),
          conversionRate: z.never().optional(),
        }),
        z.object({
          conversionRate: z.never().optional(),
          fixedAmount: z
            .object({
              configs: z
                .array(
                  z.object({
                    tierId: z
                      .string()
                      .describe(
                        'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    points: z
                      .number()
                      .int()
                      .describe('Number of points to award.')
                      .optional(),
                  })
                )
                .max(21)
                .optional(),
            })
            .describe(
              'Fixed amount of points awarded for each qualifying activity.'
            ),
        }),
        z.object({
          fixedAmount: z.never().optional(),
          conversionRate: z
            .object({
              configs: z
                .array(
                  z.object({
                    tierId: z
                      .string()
                      .describe(
                        'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    moneyAmount: z
                      .number()
                      .int()
                      .describe(
                        'The amount of money used as a reference for point calculation.\nPoints are awarded proportionally to the amount spent.\n\nFor example, if set to 10, 1 point is awarded for every 10 units of currency spent (assuming `points` is set to 1).\n\nFormula for points is: `(amount spent) / (money_amount * points)`.'
                      )
                      .optional(),
                    points: z
                      .number()
                      .int()
                      .describe(
                        'Points given for the specified `money_amount`.\nWorks in conjunction with `money_amount` to define the earning rule.\n\nFor example: If `money_amount` is 20 and `points` is 10:\n- Spending 10 units of currency earns 5 points\n- Spending 20 units of currency earns 10 points\n- Spending 30 units of currency earns 15 points'
                      )
                      .optional(),
                  })
                )
                .max(21)
                .optional(),
              field: z
                .string()
                .describe(
                  'Specifies which field in the Wix automations trigger payload [REST](https://dev.wix.com/docs/rest/business-management/automations/introduction#how-do-automations-work)|[SDK](https://dev.wix.com/docs/sdk/backend-modules/automations/triggered-events/reporting-and-canceling-events) to use for calculating points in conversion rate rules.\nFor example, if set to "priceSummary.totalAmount", the rule uses the total order amount to calculate loyalty points to be awarded.\nThis field is only applicable for automated earning rules.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Points awarded based on a conversion rate formula: `(amount spent) / (money_amount * points)`.'
            ),
        }),
      ])
    )
    .describe('Created earning rule.')
    .optional(),
});
export const GetLoyaltyEarningRuleRequest = z.object({
  _id: z
    .string()
    .describe('ID of the earning rule to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetLoyaltyEarningRuleResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Loyalty earning rule ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    sourceAppId: z
      .string()
      .describe(
        'ID of the app managing the earning rule. Can be a loyalty app ID or a Wix automations app ID.'
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    triggerAppId: z
      .string()
      .describe(
        'ID of the app that triggers point assignment. Examples: Wix Stores, Wix Bookings, Wix Events.'
      )
      .min(1)
      .max(80)
      .optional(),
    triggerActivityType: z
      .string()
      .describe(
        'Type of activity that triggers point assignment. For example, `wix-restaurants/orderSubmitted` or `birthday`.'
      )
      .min(1)
      .max(80)
      .optional(),
    title: z
      .string()
      .describe('Name of the earning rule.')
      .min(1)
      .max(50)
      .optional(),
    status: z
      .enum(['UNKNOWN', 'ACTIVE', 'PAUSED'])
      .describe('Current status of the earning rule.')
      .optional(),
    revision: z
      .string()
      .regex(/^\d+$/, 'Must be a valid UInt64 string')
      .describe(
        'Revision number, incremented by 1 each time the earning rule is updated.\nPass the latest `revision` when updating to prevent conflicting changes.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the earning rule was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the earning rule was last updated.')
      .optional()
      .nullable(),
    metadata: z
      .object({
        canBeDeleted: z
          .boolean()
          .describe('Whether the earning rule can be deleted.')
          .optional(),
      })
      .describe('Additional metadata about the earning rule.')
      .optional(),
    triggerFilters: z
      .array(
        z.object({
          fieldKey: z
            .string()
            .describe(
              'Key identifying the field in the trigger payload (e.g., "service_id", "eventId", "programId").'
            )
            .max(110)
            .optional(),
          values: z.array(z.string()).max(100).optional(),
        })
      )
      .max(100)
      .optional(),
  }),
  z.xor([
    z.object({
      fixedAmount: z.never().optional(),
      conversionRate: z.never().optional(),
    }),
    z.object({
      conversionRate: z.never().optional(),
      fixedAmount: z
        .object({
          configs: z
            .array(
              z.object({
                tierId: z
                  .string()
                  .describe(
                    'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                points: z
                  .number()
                  .int()
                  .describe('Number of points to award.')
                  .optional(),
              })
            )
            .max(21)
            .optional(),
        })
        .describe(
          'Fixed amount of points awarded for each qualifying activity.'
        ),
    }),
    z.object({
      fixedAmount: z.never().optional(),
      conversionRate: z
        .object({
          configs: z
            .array(
              z.object({
                tierId: z
                  .string()
                  .describe(
                    'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                moneyAmount: z
                  .number()
                  .int()
                  .describe(
                    'The amount of money used as a reference for point calculation.\nPoints are awarded proportionally to the amount spent.\n\nFor example, if set to 10, 1 point is awarded for every 10 units of currency spent (assuming `points` is set to 1).\n\nFormula for points is: `(amount spent) / (money_amount * points)`.'
                  )
                  .optional(),
                points: z
                  .number()
                  .int()
                  .describe(
                    'Points given for the specified `money_amount`.\nWorks in conjunction with `money_amount` to define the earning rule.\n\nFor example: If `money_amount` is 20 and `points` is 10:\n- Spending 10 units of currency earns 5 points\n- Spending 20 units of currency earns 10 points\n- Spending 30 units of currency earns 15 points'
                  )
                  .optional(),
              })
            )
            .max(21)
            .optional(),
          field: z
            .string()
            .describe(
              'Specifies which field in the Wix automations trigger payload [REST](https://dev.wix.com/docs/rest/business-management/automations/introduction#how-do-automations-work)|[SDK](https://dev.wix.com/docs/sdk/backend-modules/automations/triggered-events/reporting-and-canceling-events) to use for calculating points in conversion rate rules.\nFor example, if set to "priceSummary.totalAmount", the rule uses the total order amount to calculate loyalty points to be awarded.\nThis field is only applicable for automated earning rules.'
            )
            .optional()
            .nullable(),
        })
        .describe(
          'Points awarded based on a conversion rate formula: `(amount spent) / (money_amount * points)`.'
        ),
    }),
  ])
);
export const UpdateLoyaltyEarningRuleRequest = z.object({
  _id: z
    .string()
    .describe('Loyalty earning rule ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  earningRule: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Loyalty earning rule ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        sourceAppId: z
          .string()
          .describe(
            'ID of the app managing the earning rule. Can be a loyalty app ID or a Wix automations app ID.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        triggerAppId: z
          .string()
          .describe(
            'ID of the app that triggers point assignment. Examples: Wix Stores, Wix Bookings, Wix Events.'
          )
          .min(1)
          .max(80)
          .optional(),
        triggerActivityType: z
          .string()
          .describe(
            'Type of activity that triggers point assignment. For example, `wix-restaurants/orderSubmitted` or `birthday`.'
          )
          .min(1)
          .max(80)
          .optional(),
        title: z
          .string()
          .describe('Name of the earning rule.')
          .min(1)
          .max(50)
          .optional(),
        status: z.enum(['UNKNOWN', 'ACTIVE', 'PAUSED']).optional(),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            'Revision number, incremented by 1 each time the earning rule is updated.\nPass the latest `revision` when updating to prevent conflicting changes.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the earning rule was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the earning rule was last updated.')
          .optional()
          .nullable(),
        metadata: z
          .object({
            canBeDeleted: z
              .boolean()
              .describe('Whether the earning rule can be deleted.')
              .optional(),
          })
          .describe('Additional metadata about the earning rule.')
          .optional(),
        triggerFilters: z
          .array(
            z.object({
              fieldKey: z
                .string()
                .describe(
                  'Key identifying the field in the trigger payload (e.g., "service_id", "eventId", "programId").'
                )
                .max(110)
                .optional(),
              values: z.array(z.string()).max(100).optional(),
            })
          )
          .max(100)
          .optional(),
      }),
      z.xor([
        z.object({
          fixedAmount: z.never().optional(),
          conversionRate: z.never().optional(),
        }),
        z.object({
          conversionRate: z.never().optional(),
          fixedAmount: z
            .object({
              configs: z
                .array(
                  z.object({
                    tierId: z
                      .string()
                      .describe(
                        'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    points: z
                      .number()
                      .int()
                      .describe('Number of points to award.')
                      .optional(),
                  })
                )
                .max(21)
                .optional(),
            })
            .describe(
              'Fixed amount of points awarded for each qualifying activity.'
            ),
        }),
        z.object({
          fixedAmount: z.never().optional(),
          conversionRate: z
            .object({
              configs: z
                .array(
                  z.object({
                    tierId: z
                      .string()
                      .describe(
                        'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    moneyAmount: z
                      .number()
                      .int()
                      .describe(
                        'The amount of money used as a reference for point calculation.\nPoints are awarded proportionally to the amount spent.\n\nFor example, if set to 10, 1 point is awarded for every 10 units of currency spent (assuming `points` is set to 1).\n\nFormula for points is: `(amount spent) / (money_amount * points)`.'
                      )
                      .optional(),
                    points: z
                      .number()
                      .int()
                      .describe(
                        'Points given for the specified `money_amount`.\nWorks in conjunction with `money_amount` to define the earning rule.\n\nFor example: If `money_amount` is 20 and `points` is 10:\n- Spending 10 units of currency earns 5 points\n- Spending 20 units of currency earns 10 points\n- Spending 30 units of currency earns 15 points'
                      )
                      .optional(),
                  })
                )
                .max(21)
                .optional(),
              field: z
                .string()
                .describe(
                  'Specifies which field in the Wix automations trigger payload [REST](https://dev.wix.com/docs/rest/business-management/automations/introduction#how-do-automations-work)|[SDK](https://dev.wix.com/docs/sdk/backend-modules/automations/triggered-events/reporting-and-canceling-events) to use for calculating points in conversion rate rules.\nFor example, if set to "priceSummary.totalAmount", the rule uses the total order amount to calculate loyalty points to be awarded.\nThis field is only applicable for automated earning rules.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Points awarded based on a conversion rate formula: `(amount spent) / (money_amount * points)`.'
            ),
        }),
      ])
    )
    .describe('Earning rule to update.'),
});
export const UpdateLoyaltyEarningRuleResponse = z.object({
  earningRule: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Loyalty earning rule ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        sourceAppId: z
          .string()
          .describe(
            'ID of the app managing the earning rule. Can be a loyalty app ID or a Wix automations app ID.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        triggerAppId: z
          .string()
          .describe(
            'ID of the app that triggers point assignment. Examples: Wix Stores, Wix Bookings, Wix Events.'
          )
          .min(1)
          .max(80)
          .optional(),
        triggerActivityType: z
          .string()
          .describe(
            'Type of activity that triggers point assignment. For example, `wix-restaurants/orderSubmitted` or `birthday`.'
          )
          .min(1)
          .max(80)
          .optional(),
        title: z
          .string()
          .describe('Name of the earning rule.')
          .min(1)
          .max(50)
          .optional(),
        status: z
          .enum(['UNKNOWN', 'ACTIVE', 'PAUSED'])
          .describe('Current status of the earning rule.')
          .optional(),
        revision: z
          .string()
          .regex(/^\d+$/, 'Must be a valid UInt64 string')
          .describe(
            'Revision number, incremented by 1 each time the earning rule is updated.\nPass the latest `revision` when updating to prevent conflicting changes.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the earning rule was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the earning rule was last updated.')
          .optional()
          .nullable(),
        metadata: z
          .object({
            canBeDeleted: z
              .boolean()
              .describe('Whether the earning rule can be deleted.')
              .optional(),
          })
          .describe('Additional metadata about the earning rule.')
          .optional(),
        triggerFilters: z
          .array(
            z.object({
              fieldKey: z
                .string()
                .describe(
                  'Key identifying the field in the trigger payload (e.g., "service_id", "eventId", "programId").'
                )
                .max(110)
                .optional(),
              values: z.array(z.string()).max(100).optional(),
            })
          )
          .max(100)
          .optional(),
      }),
      z.xor([
        z.object({
          fixedAmount: z.never().optional(),
          conversionRate: z.never().optional(),
        }),
        z.object({
          conversionRate: z.never().optional(),
          fixedAmount: z
            .object({
              configs: z
                .array(
                  z.object({
                    tierId: z
                      .string()
                      .describe(
                        'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    points: z
                      .number()
                      .int()
                      .describe('Number of points to award.')
                      .optional(),
                  })
                )
                .max(21)
                .optional(),
            })
            .describe(
              'Fixed amount of points awarded for each qualifying activity.'
            ),
        }),
        z.object({
          fixedAmount: z.never().optional(),
          conversionRate: z
            .object({
              configs: z
                .array(
                  z.object({
                    tierId: z
                      .string()
                      .describe(
                        'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                      )
                      .regex(
                        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                        'Must be a valid GUID'
                      )
                      .optional()
                      .nullable(),
                    moneyAmount: z
                      .number()
                      .int()
                      .describe(
                        'The amount of money used as a reference for point calculation.\nPoints are awarded proportionally to the amount spent.\n\nFor example, if set to 10, 1 point is awarded for every 10 units of currency spent (assuming `points` is set to 1).\n\nFormula for points is: `(amount spent) / (money_amount * points)`.'
                      )
                      .optional(),
                    points: z
                      .number()
                      .int()
                      .describe(
                        'Points given for the specified `money_amount`.\nWorks in conjunction with `money_amount` to define the earning rule.\n\nFor example: If `money_amount` is 20 and `points` is 10:\n- Spending 10 units of currency earns 5 points\n- Spending 20 units of currency earns 10 points\n- Spending 30 units of currency earns 15 points'
                      )
                      .optional(),
                  })
                )
                .max(21)
                .optional(),
              field: z
                .string()
                .describe(
                  'Specifies which field in the Wix automations trigger payload [REST](https://dev.wix.com/docs/rest/business-management/automations/introduction#how-do-automations-work)|[SDK](https://dev.wix.com/docs/sdk/backend-modules/automations/triggered-events/reporting-and-canceling-events) to use for calculating points in conversion rate rules.\nFor example, if set to "priceSummary.totalAmount", the rule uses the total order amount to calculate loyalty points to be awarded.\nThis field is only applicable for automated earning rules.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              'Points awarded based on a conversion rate formula: `(amount spent) / (money_amount * points)`.'
            ),
        }),
      ])
    )
    .describe('The updated earning rule.')
    .optional(),
});
export const DeleteLoyaltyEarningRuleRequest = z.object({
  _id: z
    .string()
    .describe('ID of the earning rule to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  options: z
    .object({
      revision: z
        .string()
        .regex(/^\d+$/, 'Must be a valid UInt64 string')
        .describe(
          'Revision of the earning rule. Incremented by 1 each time the earning rule is updated.\nPass the latest `revision` when updating to prevent conflicting changes.'
        )
        .optional(),
    })
    .optional(),
});
export const DeleteLoyaltyEarningRuleResponse = z.object({});
export const DeleteAutomationEarningRuleRequest = z.object({
  _id: z
    .string()
    .describe('ID of the earning rule to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteAutomationEarningRuleResponse = z.object({});
export const ListEarningRulesRequest = z.object({
  options: z
    .object({
      triggerAppId: z
        .string()
        .describe(
          'App ID that triggers the point assignment. For example, `9a5d83fd-8570-482e-81ab-cfa88942ee60`.'
        )
        .optional()
        .nullable(),
      triggerActivityType: z
        .string()
        .describe(
          'Type of activity that triggers the point assignment. For example, `restaurants-order-is-pending`.'
        )
        .optional()
        .nullable(),
    })
    .optional(),
});
export const ListEarningRulesResponse = z.object({
  earningRules: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Loyalty earning rule ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          sourceAppId: z
            .string()
            .describe(
              'ID of the app managing the earning rule. Can be a loyalty app ID or a Wix automations app ID.'
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          triggerAppId: z
            .string()
            .describe(
              'ID of the app that triggers point assignment. Examples: Wix Stores, Wix Bookings, Wix Events.'
            )
            .min(1)
            .max(80)
            .optional(),
          triggerActivityType: z
            .string()
            .describe(
              'Type of activity that triggers point assignment. For example, `wix-restaurants/orderSubmitted` or `birthday`.'
            )
            .min(1)
            .max(80)
            .optional(),
          title: z
            .string()
            .describe('Name of the earning rule.')
            .min(1)
            .max(50)
            .optional(),
          status: z
            .enum(['UNKNOWN', 'ACTIVE', 'PAUSED'])
            .describe('Current status of the earning rule.')
            .optional(),
          revision: z
            .string()
            .regex(/^\d+$/, 'Must be a valid UInt64 string')
            .describe(
              'Revision number, incremented by 1 each time the earning rule is updated.\nPass the latest `revision` when updating to prevent conflicting changes.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the earning rule was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the earning rule was last updated.')
            .optional()
            .nullable(),
          metadata: z
            .object({
              canBeDeleted: z
                .boolean()
                .describe('Whether the earning rule can be deleted.')
                .optional(),
            })
            .describe('Additional metadata about the earning rule.')
            .optional(),
          triggerFilters: z
            .array(
              z.object({
                fieldKey: z
                  .string()
                  .describe(
                    'Key identifying the field in the trigger payload (e.g., "service_id", "eventId", "programId").'
                  )
                  .max(110)
                  .optional(),
                values: z.array(z.string()).max(100).optional(),
              })
            )
            .max(100)
            .optional(),
        }),
        z.xor([
          z.object({
            fixedAmount: z.never().optional(),
            conversionRate: z.never().optional(),
          }),
          z.object({
            conversionRate: z.never().optional(),
            fixedAmount: z
              .object({
                configs: z
                  .array(
                    z.object({
                      tierId: z
                        .string()
                        .describe(
                          'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      points: z
                        .number()
                        .int()
                        .describe('Number of points to award.')
                        .optional(),
                    })
                  )
                  .max(21)
                  .optional(),
              })
              .describe(
                'Fixed amount of points awarded for each qualifying activity.'
              ),
          }),
          z.object({
            fixedAmount: z.never().optional(),
            conversionRate: z
              .object({
                configs: z
                  .array(
                    z.object({
                      tierId: z
                        .string()
                        .describe(
                          'Tier ID. If empty, the base tier is used. See the Tiers API for more information.'
                        )
                        .regex(
                          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                          'Must be a valid GUID'
                        )
                        .optional()
                        .nullable(),
                      moneyAmount: z
                        .number()
                        .int()
                        .describe(
                          'The amount of money used as a reference for point calculation.\nPoints are awarded proportionally to the amount spent.\n\nFor example, if set to 10, 1 point is awarded for every 10 units of currency spent (assuming `points` is set to 1).\n\nFormula for points is: `(amount spent) / (money_amount * points)`.'
                        )
                        .optional(),
                      points: z
                        .number()
                        .int()
                        .describe(
                          'Points given for the specified `money_amount`.\nWorks in conjunction with `money_amount` to define the earning rule.\n\nFor example: If `money_amount` is 20 and `points` is 10:\n- Spending 10 units of currency earns 5 points\n- Spending 20 units of currency earns 10 points\n- Spending 30 units of currency earns 15 points'
                        )
                        .optional(),
                    })
                  )
                  .max(21)
                  .optional(),
                field: z
                  .string()
                  .describe(
                    'Specifies which field in the Wix automations trigger payload [REST](https://dev.wix.com/docs/rest/business-management/automations/introduction#how-do-automations-work)|[SDK](https://dev.wix.com/docs/sdk/backend-modules/automations/triggered-events/reporting-and-canceling-events) to use for calculating points in conversion rate rules.\nFor example, if set to "priceSummary.totalAmount", the rule uses the total order amount to calculate loyalty points to be awarded.\nThis field is only applicable for automated earning rules.'
                  )
                  .optional()
                  .nullable(),
              })
              .describe(
                'Points awarded based on a conversion rate formula: `(amount spent) / (money_amount * points)`.'
              ),
          }),
        ])
      )
    )
    .optional(),
});
